import { Badge } from 'cpool-components/general/Badge';
import { Tooltip, TooltipContent, TooltipTrigger } from 'cpool-components/general/Tooltip';
import { Truncate } from 'cpool-components/general/Truncate';
import { COLORS } from 'cpool-components/types';
import type {
    SchemaLoanRequestResolver_Find,
    SchemaLoanRequestResolver_GetDuplicateInfoByLead,
    SchemaLoanRequestResolver_GetSummary,
} from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';
import { LuTriangleAlert, LuUserRound, LuUsersRound } from 'react-icons/lu';

import { type RowHighlightProps } from '../../useRowHighlightDialog';
import { ActionButtons } from '../ActionButtons';
import { cn } from '~/utils/cnTailwind.ts';

type PropsContactInfo = {
    lead:
        | SchemaLoanRequestResolver_GetSummary[number]
        | (SchemaLoanRequestResolver_Find[number] & {
              antragsteller1: NonNullable<SchemaLoanRequestResolver_Find[number]['antragsteller1']>;
          })
        | SchemaLoanRequestResolver_GetDuplicateInfoByLead[number];
    className?: string;
    showActionButtons?: boolean;
    showOnlyDuplicateInfo?: boolean;
} & RowHighlightProps;

export const ContactInfo: React.FC<PropsContactInfo> = ({
    className,
    lead,
    showActionButtons = true,
    showOnlyDuplicateInfo = false,
    ...highlightProps
}) => {
    const { t } = useTranslation();
    const {
        antragsteller1: {
            personendaten: { nachname, vorname },
        },
    } = lead;

    const getLastNote = () => {
        if (!('notes' in lead) || lead.notes == null) return null;
        const sortedNotes = lead.notes.sort((a, b) => {
            if (a.favored !== b.favored) return a.favored ? -1 : 1;
            return b.timestamp - a.timestamp;
        });
        return sortedNotes.length > 0 && sortedNotes[0].content;
    };

    const lastNote = getLastNote();

    const secondApplicantHasNotAgreed =
        !!('antragsteller2' in lead) &&
        lead.antragsteller2 !== null &&
        'applicant2DataProtectionAgreed' in lead &&
        lead.applicant2DataProtectionAgreed !== true;

    return (
        <div className="flex justify-start py-3 max-w-[300px]">
            <div className={cn('flex items-center px-4 max-w-[150px] min-w-[150px]', className)}>
                <div className={cn('flex flex-col text-sm text-gray-800 min-w-[130px]')}>
                    <div className="flex items-center gap-2 min-w-0">
                        <Truncate
                            showTooltip
                            maxWidth="130px"
                            className="text-sm text-midnight font-semibold"
                            text={`${vorname} ${nachname}`}
                        />
                    </div>
                    <Truncate
                        showTooltip
                        maxWidth="130px"
                        className="text-sm text-gray-800"
                        text={
                            lead.antragsteller1.wohnsituation?.anschrift?.ort
                                ? lead.antragsteller1.wohnsituation.anschrift.ort
                                : ''
                        }
                    >
                        {lead.antragsteller1.wohnsituation?.anschrift?.ort
                            ? lead.antragsteller1.wohnsituation.anschrift.ort
                            : ''}
                    </Truncate>
                    {lastNote && (
                        <div className="flex h-full w-full items-end">
                            <Truncate
                                showTooltip
                                maxWidth="130px"
                                className="text-sm text-grey-note select-none z-[15]"
                            >
                                {lastNote}
                            </Truncate>
                        </div>
                    )}
                </div>
            </div>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div className="ml-auto h-fit self-start">
                        <Badge
                            color={secondApplicantHasNotAgreed ? COLORS.YELLOW : COLORS.GREY}
                            className="leading-none [&>span]:inline-flex [&>span]:items-center [&>span]:justify-center [&>span]:gap-1"
                        >
                            {lead.antragsteller2 ? '2' : '1'}
                            {lead.antragsteller2 ? <LuUsersRound /> : <LuUserRound />}
                            {secondApplicantHasNotAgreed && <LuTriangleAlert size={13} />}
                        </Badge>
                    </div>
                </TooltipTrigger>
                {secondApplicantHasNotAgreed && (
                    <TooltipContent side="bottom" align="end">
                        {t('LEAD_DETAILS_GENERAL.DATA_PROTECTION_AGREEMENT_WARNING')}
                    </TooltipContent>
                )}
            </Tooltip>
            {showActionButtons && (
                <ActionButtons lead={lead} showOnlyDuplicateInfo={showOnlyDuplicateInfo} {...highlightProps} />
            )}
        </div>
    );
};
